/**
 * Equipe da demonstração (SPEC-v2 2.2, 7.1 item 6): nomes fictícios (SPEC 8.9), domínio
 * `exemplo.com.br`, ids iguais aos da simulação (`packages/sim/src/defaults.ts`), para o Painel, as
 * Automações e o Estúdio falarem da mesma pessoa. A exceção é o expert, Tiago Brito, dono do produto.
 *
 * **Nenhuma credencial aqui.** O dono cria a própria senha em `/configurar` (nome e e-mail também
 * são dele); os demais entram por convite gerado em Equipe. Um teste garante que nenhum campo deste
 * arquivo se pareça com senha (critério 2 da V2 11).
 */
import type { Id, Role, StudioFunction } from '@rota/contracts';

export interface SeedPerson {
  id: Id;
  name: string;
  initials: string;
  email: string;
  role: Role;
  functions: StudioFunction[];
}

/** O lugar do dono existe desde o início (id da simulação); nome e e-mail vêm de `/configurar`. */
export const OWNER_ID: Id = 'u_lucas';

export const SEED_TEAM: readonly SeedPerson[] = [
  {
    id: 'u_rafael',
    name: 'Rafael Moura',
    initials: 'RM',
    email: 'rafael@exemplo.com.br',
    role: 'partner',
    functions: ['producer'],
  },
  {
    id: 'u_bruna',
    name: 'Bruna Teixeira',
    initials: 'BT',
    email: 'bruna@exemplo.com.br',
    role: 'traffic',
    functions: ['traffic'],
  },
  {
    id: 'u_diego',
    name: 'Diego Lima',
    initials: 'DL',
    email: 'diego@exemplo.com.br',
    role: 'creative',
    functions: ['copy'],
  },
  {
    id: 'u_julia',
    name: 'Júlia Ramos',
    initials: 'JR',
    email: 'julia@exemplo.com.br',
    role: 'creative',
    functions: ['editor'],
  },
  {
    id: 'u_camila',
    name: 'Camila Souza',
    initials: 'CS',
    email: 'camila@exemplo.com.br',
    role: 'support',
    functions: ['social'],
  },
  {
    id: 'u_tiago',
    name: 'Tiago Brito',
    initials: 'TB',
    email: 'tiago@exemplo.com.br',
    role: 'expert',
    functions: ['expert'],
  },
  {
    id: 'u_tech',
    name: 'Henrique Dias',
    initials: 'HD',
    email: 'henrique@exemplo.com.br',
    role: 'tech',
    functions: [],
  },
];

/** Pessoa de demonstração para `?demo=<papel>` (só no dev server; ADR-009). */
export function demoPersonFor(role: Role): SeedPerson {
  if (role === 'owner')
    return {
      id: OWNER_ID,
      name: 'Lucas Almeida',
      initials: 'LA',
      email: 'lucas@exemplo.com.br',
      role: 'owner',
      functions: ['producer', 'compliance'],
    };
  if (role === 'tv')
    return {
      id: 'u_tv',
      name: 'TV do escritório',
      initials: 'TV',
      email: 'tv@exemplo.com.br',
      role,
      functions: [],
    };
  const hit = SEED_TEAM.find((p) => p.role === role);
  if (!hit) throw new Error(`Sem pessoa de demonstração para ${role}`);
  return hit;
}

/** "Lucas Almeida" → "LA"; "Tiago" → "TI". */
export function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  const first = parts[0] ?? '';
  if (parts.length === 1) return first.slice(0, 2).toLocaleUpperCase('pt-BR');
  const last = parts[parts.length - 1] ?? '';
  return `${first[0] ?? ''}${last[0] ?? ''}`.toLocaleUpperCase('pt-BR');
}
